/**
 * Stage content for the new-calendar flow: the template grid before a
 * template is picked (wrapped in a card, "See all demos" link), the demo
 * preview once one is (a second card: disclosure header, toolbar, the
 * preview wrapped in a browser-chrome frame). Mounted as `stage` inside
 * BuilderShell, which supplies the grey stage background/padding.
 * Spec: assets/handoffs/design_handoff_new_calendar_page/FIXES-demo-preview.md
 *
 * @param {Array<{slug:string,title:string,pitch:string,tag:string,thumbnailSrc:string,features:string[],previewUrl?:string,demoSlug:string}>} templates
 * @param {string} selectedSlug
 * @param {function} onSelectTemplate - (slug) => void
 * @param {function} onBackToGrid - () => void, clears the selection
 */
import React, { useMemo, useState } from "react";
import Tabs from "../../theme/untitled/Tabs/Tabs";
import RadioCardGrid from "../../theme/untitled/RadioCardGrid/RadioCardGrid";
import Icon from "../../theme/untitled/icons/Icon";
import DemoBrowserFrame from "./DemoBrowserFrame";
import styles from "./TemplateStage.module.scss";

const VIEWPORT_TABS = [
  { key: "desktop", label: "Desktop" },
  { key: "mobile", label: "Mobile" },
];

const DEMOS_INDEX_URL = "https://revisual.io/demos/";

const TemplateStage = ({
  templates,
  selectedSlug,
  onSelectTemplate,
  onBackToGrid,
}) => {
  const [viewport, setViewport] = useState("desktop");
  const selected = templates.find((t) => t.slug === selectedSlug);

  const radioCardItems = useMemo(
    () =>
      templates.map((t) => ({
        key: t.slug,
        name: t.title,
        pitch: t.pitch,
        tag: t.tag,
        thumbnailSrc: t.thumbnailSrc,
      })),
    [templates]
  );

  if (!selected) {
    return (
      <div className={styles.chooserCard}>
        <div className={styles.cardHeader}>
          <div>
            <h2 className={styles.cardTitle}>Choose a template</h2>
            <p className={styles.cardSubtitle}>
              Pick the layout closest to what you want. You can fine-tune the
              design afterwards. To use a different template later, create a new
              calendar.
            </p>
          </div>
          <a
            className={styles.demosLink}
            href={`${DEMOS_INDEX_URL}cards-grid/`}
            target="_blank"
            rel="noopener"
          >
            See all demos
            <Icon name="ArrowUpRight" size={16} />
          </a>
        </div>
        <div className={styles.chooserBody}>
          <RadioCardGrid
            items={radioCardItems}
            value={selectedSlug}
            onChange={onSelectTemplate}
          />
        </div>
      </div>
    );
  }

  const featureItems = selected.features?.map((feature) => (
    <span key={feature} className={styles.descriptionItem}>
      <Icon name="Check" size={16} className={styles.descriptionIcon} />
      {feature.replace(/\.$/, "")}
    </span>
  ));

  return (
    <>
      <div className={styles.backRow}>
        <button
          type="button"
          className={styles.backButton}
          onClick={onBackToGrid}
        >
          <Icon name="ArrowLeft" size={16} />
          All templates
        </button>
      </div>
      <div className={styles.previewCard}>
        <div className={styles.cardHeader}>
          <div>
            <h2 className={styles.cardTitle}>
              {selected.title} — Preview with demo events
            </h2>
            <p className={styles.cardSubtitle}>
              These events are for illustration only. Your own appear once you
              connect a calendar.
            </p>
          </div>
          <a
            className={styles.demosLink}
            href={`${DEMOS_INDEX_URL}${selected.demoSlug}`}
            target="_blank"
            rel="noopener"
          >
            See more demos
            <Icon name="ArrowUpRight" size={16} />
          </a>
        </div>
        <div className={styles.toolbar}>
          {selected.features?.length > 0 && (
            <div className={styles.descriptions}>{featureItems}</div>
          )}
          <Tabs
            variant="segmented-compact"
            items={VIEWPORT_TABS}
            value={viewport}
            onChange={setViewport}
            className={styles.viewportTabs}
          />
        </div>
        <div className={styles.previewBody}>
          <DemoBrowserFrame
            address={`demo.revisual.io/${selected.demoSlug}`}
            mobile={viewport === "mobile"}
          >
            {selected.previewUrl ? (
              <iframe
                title={`${selected.title} sample preview`}
                src={selected.previewUrl}
                className={styles.canvasFrame}
              />
            ) : (
              <div className={styles.canvasPlaceholder}>
                <img
                  src={selected.thumbnailSrc}
                  alt=""
                  className={styles.canvasThumb}
                />
              </div>
            )}
          </DemoBrowserFrame>
        </div>
      </div>
    </>
  );
};

export default TemplateStage;
